import { useEffect, useState } from 'react'
import { PortableText } from '@portabletext/react'
import ReactPlayer from 'react-player'
import { motion } from 'framer-motion'
import { Disclosure } from '@headlessui/react'
import { client, urlFor } from '../lib/sanity'
import * as SI from 'react-icons/si'

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.06, delayChildren: 0.12 }
    }
}

const item = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0 }
}

const Icon = ({ slug, className = 'h-6 w-6' }) => {
    if (!slug) return null
    const key = 'Si' + slug.replace(/(^|-)(\w)/g, (_, __, c) => c.toUpperCase())
    const Cmp = SI[key]
    return Cmp ? <Cmp className={className} /> : null
}

const Ring = ({ percent = 80, children }) => {
    const safe = Math.max(0, Math.min(100, percent))
    return (
        <div className="relative w-20 h-20">
            <div className="absolute inset-0 rounded-full opacity-20 bg-accent" />
            <motion.div
                className="absolute inset-0 rounded-full"
                initial={{ '--p': '0%' }}
                whileInView={{ '--p': `${safe}%` }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
                style={{ background: 'conic-gradient(#FF5F1F var(--p), transparent 0)' }}
                aria-hidden="true"
            />
            <div className="absolute inset-[6px] rounded-full bg-white dark:bg-black flex items-center justify-center">
                {children}
            </div>
        </div>
    )
}

const TimelineItemContent = ({ t }) => {
    return (
        <Disclosure>
            {({ open }) => (
                <motion.div
                    whileHover={{ y: -4, scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="border-2 border-black dark:border-white bg-white dark:bg-zinc-950 shadow-brutal-sm hover:shadow-brutal hover:border-accent dark:hover:border-accent transition-all duration-300"
                >
                    <Disclosure.Button className="w-full flex items-start justify-between gap-3 text-left px-4 md:px-5 py-3 md:py-4">
                        <div className="flex-1 min-w-0">
                            {t.link ? (
                                <a
                                    href={t.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-semibold text-accent hover:underline underline-offset-4 decoration-2 inline-flex items-center gap-1"
                                >
                                    {t.title}{t.org && <span> · {t.org}</span>}
                                    <span className="text-xs opacity-70 flex-shrink-0">↗</span>
                                </a>
                            ) : (
                                <h3 className="font-semibold text-black dark:text-white">
                                    {t.title}{t.org && <span className="text-zinc-700 dark:text-zinc-300"> · {t.org}</span>}
                                </h3>
                            )}
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                {t.dateRange && (
                                    <span className="inline-block px-2.5 py-1 text-xs font-mono font-medium border-2 border-accent text-accent">
                                        {t.dateRange}
                                    </span>
                                )}
                                {t.location && (
                                    <span className="inline-block px-2.5 py-1 text-xs font-mono font-medium border-2 border-black dark:border-white text-zinc-700 dark:text-zinc-300">
                                        📍 {t.location}
                                    </span>
                                )}
                            </div>
                        </div>
                        <svg
                            className={`h-5 w-5 mt-1 shrink-0 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-4 md:px-5 pb-4 md:pb-5">
                        {t.description?.length ? (
                            <div className="prose prose-sm md:prose dark:prose-invert">
                                <PortableText value={t.description} components={PT} />
                            </div>
                        ) : (
                            <p className="text-sm text-zinc-600 dark:text-zinc-300">No additional details provided.</p>
                        )}
                    </Disclosure.Panel>
                </motion.div>
            )}
        </Disclosure>
    )
}

const PT = {
    block: {
        normal: ({ children }) => (
            <p className="text-inherit leading-relaxed tracking-[0.005em] text-justify hyphens-auto">
                {children}
            </p>
        ),
        h2: ({ children }) => (
            <h2 className="mt-8 mb-3 text-2xl md:text-3xl font-semibold text-zinc-900 dark:text-zinc-100 text-balance">
                {children}
            </h2>
        ),
        h3: ({ children }) => (
            <h3 className="mt-7 mb-2 text-xl md:text-2xl font-semibold text-zinc-900 dark:text-zinc-100 text-balance">
                {children}
            </h3>
        ),
        blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-accent pl-4 italic text-zinc-700 dark:text-zinc-300">
                {children}
            </blockquote>
        ),
        hr: () => <hr className="my-8 border-zinc-200 dark:border-zinc-700" />,
        list: ({ children }) => <ul className="list-disc ml-6 space-y-1">{children}</ul>,
        number: ({ children }) => <ol className="list-decimal ml-6 space-y-1">{children}</ol>,
        code: ({ children }) => (
            <pre className="bg-zinc-100 dark:bg-zinc-900 border-2 border-black dark:border-white p-4 overflow-x-auto text-sm">
                <code>{children}</code>
            </pre>
        )
    },
    marks: {
        em: ({ children }) => <em className="italic">{children}</em>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        link: ({ value, children }) => (
            <a
                href={value?.href}
                target="_blank"
                rel="noreferrer"
                className="text-accent underline decoration-2 underline-offset-2 hover:no-underline"
            >
                {children}
            </a>
        )
    }
}

export default function About() {
    const [data, setData] = useState(null)
    const [ready, setReady] = useState(false)
    const [platformFilter, setPlatformFilter] = useState('All')
    const [skillCategory, setSkillCategory] = useState('Language')

    useEffect(() => {
        client.fetch(query).then(setData)
    }, [])

    useEffect(() => {
        if (!data) return
        const id = requestAnimationFrame(() => setReady(true))
        return () => cancelAnimationFrame(id)
    }, [data])

    if (!data) {
        return (
            <div className="min-h-screen bg-white dark:bg-zinc-950">
                <div className="mx-auto max-w-5xl p-6 space-y-6 animate-pulse">
                    <div className="h-8 w-1/3 bg-zinc-200 dark:bg-zinc-800" />
                    <div className="aspect-video w-full max-w-2xl bg-zinc-200 dark:bg-zinc-800" />
                    <div className="h-6 w-2/3 bg-zinc-200 dark:bg-zinc-800" />
                    <div className="space-y-3">
                        <div className="h-4 w-full bg-zinc-200 dark:bg-zinc-800" />
                        <div className="h-4 w-5/6 bg-zinc-200 dark:bg-zinc-800" />
                        <div className="h-4 w-4/5 bg-zinc-200 dark:bg-zinc-800" />
                    </div>
                    <div className="h-14 w-full bg-zinc-200 dark:bg-zinc-800" />
                    <div className="h-14 w-full bg-zinc-200 dark:bg-zinc-800" />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <motion.div
                key={data?._rev || 'about-loaded'}
                initial="hidden"
                animate={ready ? 'show' : 'hidden'}
                variants={container}
                className="mx-auto max-w-5xl p-6 space-y-10 text-zinc-900 dark:text-zinc-100"
            >
                {data.videoIntro?.url && (
                    <section aria-labelledby="intro">
                        <h2 id="intro" className="text-xl font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
                            Video Intro
                        </h2>
                        <div className="aspect-video w-full overflow-hidden border-2 border-black dark:border-white">
                            <ReactPlayer url={data.videoIntro.url} width="100%" height="100%" controls />
                        </div>
                        {data.videoIntro.caption && (
                            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">{data.videoIntro.caption}</p>
                        )}
                    </section>
                )}

                {!!data.timeline?.length && (
                    <section aria-labelledby="timeline" className="scroll-mt-20">
                        <h2 id="timeline" className="text-2xl md:text-3xl font-bold mb-4 text-zinc-900 dark:text-zinc-100">
                            Timeline
                        </h2>

                        <ol className="relative md:hidden pl-10 border-l-2 border-zinc-300 dark:border-zinc-700">
                            {data.timeline.map((t, i) => (
                                <motion.li
                                    key={i}
                                    variants={item}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: '-50px' }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    className="relative mb-6"
                                >
                                    <TimelineItemContent t={t} />
                                </motion.li>
                            ))}
                        </ol>

                        <ol className="hidden md:relative md:flex md:flex-col md:items-center">
                            <div
                                className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-zinc-300 dark:bg-zinc-700 -translate-x-1/2"
                                aria-hidden="true"
                            />

                            {data.timeline.map((t, i) => {
                                const isLeft = i % 2 === 0
                                return (
                                    <motion.li
                                        key={i}
                                        variants={item}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true, margin: '-50px' }}
                                        transition={{ duration: 0.5, delay: i * 0.1 }}
                                        className={`relative w-full mb-8 flex ${isLeft ? 'justify-start' : 'justify-end'} group`}
                                    >
                                        <div className="w-[calc(50%-2rem)]">
                                            <TimelineItemContent t={t} />
                                        </div>

                                        <motion.div
                                            className="absolute left-1/2 top-8 -translate-x-1/2 h-4 w-4 bg-accent rounded-full border-4 border-white dark:border-zinc-950 shadow-md transition-all duration-300"
                                            whileHover={{ scale: 1.3 }}
                                            whileInView={() => ({
                                                boxShadow: ['0 0 0 0 rgba(255, 95, 31, 0.4)', '0 0 0 8px rgba(255, 95, 31, 0)']
                                            })}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                            aria-hidden="true"
                                        />
                                    </motion.li>
                                )
                            })}
                        </ol>
                    </section>
                )}

                {!!data.story?.length && (
                    <section aria-labelledby="story">
                        <Disclosure>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="w-full flex items-center justify-between bg-white dark:bg-zinc-950 px-5 py-4 border-2 border-black dark:border-white shadow-brutal-sm hover:bg-accent hover:text-black transition-colors mb-3">
                                        <h2 id="story" className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                                            My Story
                                        </h2>
                                        <span className="text-2xl font-light leading-none text-zinc-700 dark:text-zinc-300">
                                            {open ? '−' : '+'}
                                        </span>
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="mt-0 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4">
                                        <div className="mx-auto max-w-3xl prose prose-base md:prose-lg prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-headings:text-balance prose-p:text-pretty prose-p:text-justify prose-p:hyphens-auto prose-a:no-underline hover:prose-a:underline prose-a:underline-offset-4 prose-img:border-2 prose-img:border-black dark:prose-img:border-white prose-hr:my-8 hyphens-auto [&_p:first-of-type:first-letter]:text-5xl [&_p:first-of-type:first-letter]:font-semibold [&_p:first-of-type:first-letter]:leading-[0.9] [&_p:first-of-type:first-letter]:mr-2 [&_p:first-of-type:first-letter]:text-accent">
                                            <PortableText value={data.story} components={PT} />
                                        </div>
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </section>
                )}

                {!!data.skills?.length && (
                    <section aria-labelledby="skills" className="scroll-mt-24">
                        <Disclosure>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="w-full flex items-center justify-between bg-white dark:bg-zinc-950 px-5 py-4 border-2 border-black dark:border-white shadow-brutal-sm hover:bg-accent hover:text-black transition-colors mb-3">
                                        <h2 id="skills" className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                                            Skills &amp; Tools
                                        </h2>
                                        <span className="text-2xl font-light leading-none text-zinc-700 dark:text-zinc-300">
                                            {open ? '−' : '+'}
                                        </span>
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="mt-0 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4">
                                        {(() => {
                                            const groups = Array.from(new Set(['All', ...data.skills.map(s => s.group || 'Other')]))
                                            return (
                                                <div className="w-full -mx-2 mb-5 flex gap-2 sm:gap-3 pb-1 justify-center flex-wrap overflow-x-auto sm:overflow-x-visible [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                                    {groups.map(g => (
                                                        <button
                                                            key={g}
                                                            type="button"
                                                            onClick={() => setSkillCategory(g)}
                                                            className={`whitespace-nowrap border-2 my-2 px-3.5 py-1.5 text-sm font-mono transition-colors focus:outline-none focus:ring-2 focus:ring-accent ${skillCategory === g
                                                                    ? 'bg-accent text-black border-black dark:border-white'
                                                                    : 'bg-white dark:bg-zinc-950 text-zinc-700 dark:text-zinc-300 border-black dark:border-white hover:bg-accent hover:text-black'
                                                                }`}
                                                            aria-pressed={skillCategory === g}
                                                        >
                                                            {g}
                                                        </button>
                                                    ))}
                                                </div>
                                            )
                                        })()}

                                        {skillCategory === 'Platform' && (
                                            <div className="mb-4 flex flex-wrap items-center gap-2">
                                                {['All', ...Array.from(new Set(data.skills.filter(s => (s.group || 'Other') === 'Platform').map(s => s.name)))].map(opt => (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        onClick={() => setPlatformFilter(opt)}
                                                        className={`text-xs border-2 px-2.5 py-1 font-mono transition-colors focus:outline-none focus:ring-2 focus:ring-accent ${platformFilter === opt
                                                                ? 'bg-white text-accent border-accent dark:bg-zinc-950'
                                                                : 'bg-white dark:bg-zinc-950 text-zinc-700 dark:text-zinc-300 border-zinc-300 dark:border-zinc-700 hover:bg-accent hover:text-black'
                                                            }`}
                                                    >
                                                        {opt}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {(() => {
                                            const byCategory = skillCategory === 'All' ? data.skills : data.skills.filter(s => (s.group || 'Other') === skillCategory)
                                            const items = skillCategory === 'Platform' && platformFilter !== 'All'
                                                ? byCategory.filter(s => s.name === platformFilter)
                                                : byCategory

                                            return (
                                                <motion.div
                                                    key={`${skillCategory}-${platformFilter}`}
                                                    initial="hidden"
                                                    whileInView="show"
                                                    viewport={{ once: false, amount: 0.2 }}
                                                    variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
                                                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5"
                                                >
                                                    {items.map((s, i) => (
                                                        <motion.a
                                                            key={`${i}-${s.name}`}
                                                            href={s.url || undefined}
                                                            target={s.url ? '_blank' : undefined}
                                                            rel={s.url ? 'noreferrer' : undefined}
                                                            initial={{ opacity: 0, y: 8, scale: 0.98 }}
                                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                                            transition={{ duration: 0.25, delay: i * 0.05 }}
                                                            whileHover={{ y: -3, scale: 1.02 }}
                                                            className="group relative border-2 border-black dark:border-white p-4 bg-white dark:bg-zinc-950 hover:bg-accent hover:shadow-brutal transition-all shadow-brutal-sm"
                                                        >
                                                            <div className="flex flex-col items-center text-center">
                                                                <Ring percent={s.level ?? 80}>
                                                                    <Icon slug={s.icon} className="h-7 w-7 text-zinc-700 dark:text-zinc-300 group-hover:text-black transition-colors" />
                                                                </Ring>
                                                                <div className="mt-3 font-medium text-black dark:text-white group-hover:text-black transition-colors">
                                                                    {s.name}
                                                                </div>
                                                            </div>
                                                        </motion.a>
                                                    ))}
                                                </motion.div>
                                            )
                                        })()}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </section>
                )}

                {!!data.certifications?.length && (
                    <section aria-labelledby="certs">
                        <Disclosure>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="w-full flex items-center justify-between bg-white dark:bg-zinc-950 px-5 py-4 border-2 border-black dark:border-white shadow-brutal-sm hover:bg-accent hover:text-black transition-colors mb-3">
                                        <h2 id="certs" className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                                            Certifications
                                        </h2>
                                        <span className="text-2xl font-light leading-none text-zinc-700 dark:text-zinc-300">
                                            {open ? '−' : '+'}
                                        </span>
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="mt-0 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4">
                                        <ul className="space-y-3 mt-0">
                                            {data.certifications.map((c, i) => (
                                                <li key={i} className="flex items-start gap-3">
                                                    <span className="mt-2 inline-block h-2 w-2 rounded-full bg-accent" aria-hidden="true" />
                                                    <div>
                                                        <div className="font-medium">
                                                            {c.url ? (
                                                                <a
                                                                    className="text-accent underline decoration-2 hover:no-underline"
                                                                    href={c.url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    {c.name}
                                                                </a>
                                                            ) : (
                                                                c.name
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-zinc-700 dark:text-zinc-300">
                                                            {[c.issuer, c.date?.slice(0, 7)].filter(Boolean).join(' • ')}
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </section>
                )}

                {!!data.accomplishments?.length && (
                    <section aria-labelledby="acc">
                        <Disclosure>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="w-full flex items-center justify-between bg-white dark:bg-zinc-950 px-5 py-4 border-2 border-black dark:border-white shadow-brutal-sm hover:bg-accent hover:text-black transition-colors mb-3">
                                        <h2 id="acc" className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                                            Other Accomplishments
                                        </h2>
                                        <span className="text-2xl font-light leading-none text-zinc-700 dark:text-zinc-300">
                                            {open ? '−' : '+'}
                                        </span>
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="mt-0 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4">
                                        <ul className="space-y-3 mt-0">
                                            {data.accomplishments.map((a, i) => (
                                                <li key={i} className="border-2 border-black dark:border-white p-4 bg-white dark:bg-zinc-950">
                                                    <div className="font-medium">
                                                        {a.url ? (
                                                            <a
                                                                href={a.url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-accent underline-offset-4 decoration-2 hover:underline"
                                                            >
                                                                {a.title}
                                                            </a>
                                                        ) : (
                                                            a.title
                                                        )}
                                                    </div>
                                                    {a.date && <div className="text-sm text-zinc-700 dark:text-zinc-300">{a.date}</div>}
                                                    {a.summary && <p className="mt-1 text-zinc-700 dark:text-zinc-300">{a.summary}</p>}
                                                </li>
                                            ))}
                                        </ul>
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </section>
                )}

                {!!data.volunteering?.length && (
                    <section aria-labelledby="vol">
                        <Disclosure>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="w-full flex items-center justify-between bg-white dark:bg-zinc-950 px-5 py-4 border-2 border-black dark:border-white shadow-brutal-sm hover:bg-accent hover:text-black transition-colors mb-3">
                                        <h2 id="vol" className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                                            Volunteering
                                        </h2>
                                        <span className="text-2xl font-light leading-none text-zinc-700 dark:text-zinc-300">
                                            {open ? '−' : '+'}
                                        </span>
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="mt-0 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4">
                                        <ul className="space-y-4 mt-0">
                                            {data.volunteering.map((v, i) => (
                                                <li key={i} className="border-2 border-black dark:border-white p-4 bg-white dark:bg-zinc-950">
                                                    <div className="font-medium">
                                                        {v.url ? (
                                                            <a
                                                                href={v.url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-accent underline-offset-4 decoration-2 hover:underline"
                                                            >
                                                                {v.role}{v.org && <span> · {v.org}</span>}
                                                            </a>
                                                        ) : (
                                                            <>
                                                                {v.role}{v.org && <span className="text-zinc-700 dark:text-zinc-300"> · {v.org}</span>}
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-zinc-700 dark:text-zinc-300">{v.dateRange}</div>
                                                    {v.summary?.length && (
                                                        <div className="prose dark:prose-invert mt-2">
                                                            <PortableText value={v.summary} components={PT} />
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </section>
                )}

                {!!data.hobbies?.length && (
                    <section aria-labelledby="hobbies">
                        <Disclosure>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="w-full flex items-center justify-between bg-white dark:bg-zinc-950 px-5 py-4 border-2 border-black dark:border-white shadow-brutal-sm hover:bg-accent hover:text-black transition-colors mb-3">
                                        <h2 id="hobbies" className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                                            Hobbies
                                        </h2>
                                        <span className="text-2xl font-light leading-none text-zinc-700 dark:text-zinc-300">
                                            {open ? '−' : '+'}
                                        </span>
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="mt-0 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4">
                                        <div className="mt-3 grid sm:grid-cols-2 gap-4">
                                            {data.hobbies.map((h, i) => (
                                                <div key={i} className="flex items-center gap-3 border-2 border-black dark:border-white p-4 bg-white dark:bg-zinc-950">
                                                    {h.emoji && <span className="text-2xl">{h.emoji}</span>}
                                                    {h.image && (
                                                        <img
                                                            src={urlFor(h.image).width(80).height(80).url()}
                                                            alt={h.name}
                                                            className="w-16 h-16 rounded object-cover"
                                                        />
                                                    )}
                                                    <div>
                                                        <div className="font-medium">{h.name}</div>
                                                        {h.description && (
                                                            <p className="text-sm text-zinc-700 dark:text-zinc-300">{h.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </section>
                )}
            </motion.div>
        </div>
    )
}

const query = `*[_type=="about"][0]{ 
    title, profileImage, videoIntro,
    timeline[]{title,org,dateRange,location,description,icon,link},
    "story": coalesce(story, content)[]{...,asset->,"url":coalesce(url,asset->url)},
    skills[]{name,level,icon,url,group},
    certifications[]{name,issuer,date,url},
    accomplishments[]{title,summary,date,url},
    volunteering[]{role,org,dateRange,summary,url},
    hobbies[]{name,description,emoji,image}
}`